package com.example.hukakmp.data

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.Timestamp
import dev.gitlive.firebase.firestore.firestore
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.Serializable
import kotlin.time.Instant

private const val QUOTA_COLLECTION = "user_quotas"
private const val CHAT_DAILY_LIMIT = 5
private const val CHAT_PREMIUM_LIMIT = 999
private const val PHOTO_DAILY_LIMIT = 1

@Serializable
private data class AiQuotaDocument(
    val dailyChatsUsed: Int = 0,
    val dailyPhotosUsed: Int = 0,
    val lastResetDate: Timestamp? = null,
    val isPremium: Boolean = false,
    val createdAt: Timestamp? = null,
)

data class AiQuotaState(
    val chatsRemaining: Int = CHAT_DAILY_LIMIT,
    val chatsTotal: Int = CHAT_DAILY_LIMIT,
    val photosRemaining: Int = PHOTO_DAILY_LIMIT,
    val photosTotal: Int = PHOTO_DAILY_LIMIT,
    val isPremium: Boolean = false,
)

class AiQuotaRepository {
    private val firestore = Firebase.firestore

    /**
     * Lee y, si corresponde, resetea la cuota dentro de una transacción.
     * Esto evita que Web y Android puedan pisarse si abren/consumen al mismo tiempo.
     */
    suspend fun getState(): AiQuotaState {
        val uid = Firebase.auth.currentUser?.uid ?: return AiQuotaState()
        val ref = firestore.document("$QUOTA_COLLECTION/$uid")

        return try {
            firestore.runTransaction {
                val snapshot = get(ref)
                val now = Timestamp.now()

                val current = if (snapshot.exists) {
                    snapshot.data(AiQuotaDocument.serializer())
                } else {
                    AiQuotaDocument(
                        lastResetDate = now,
                        createdAt = now,
                    )
                }

                val normalized = if (
                    current.lastResetDate == null ||
                    isNewLocalDay(current.lastResetDate)
                ) {
                    current.copy(
                        dailyChatsUsed = 0,
                        dailyPhotosUsed = 0,
                        lastResetDate = now,
                        createdAt = current.createdAt ?: now,
                    )
                } else {
                    current
                }

                if (!snapshot.exists || normalized != current) {
                    set(
                        documentRef = ref,
                        strategy = AiQuotaDocument.serializer(),
                        data = normalized,
                        merge = true,
                    )
                }

                normalized.toState()
            }
        } catch (_: Exception) {
            // Mantiene la UI disponible ante un fallo de red. El consumo real
            // vuelve a intentar de forma transaccional antes de descontar.
            AiQuotaState()
        }
    }

    suspend fun consumeChat(): AiQuotaState? {
        val uid = Firebase.auth.currentUser?.uid ?: return null
        val ref = firestore.document("$QUOTA_COLLECTION/$uid")

        return try {
            firestore.runTransaction {
                val snapshot = get(ref)
                val now = Timestamp.now()

                val current = if (snapshot.exists) {
                    snapshot.data(AiQuotaDocument.serializer())
                } else {
                    AiQuotaDocument(
                        lastResetDate = now,
                        createdAt = now,
                    )
                }

                val normalized = if (
                    current.lastResetDate == null ||
                    isNewLocalDay(current.lastResetDate)
                ) {
                    current.copy(
                        dailyChatsUsed = 0,
                        dailyPhotosUsed = 0,
                        lastResetDate = now,
                        createdAt = current.createdAt ?: now,
                    )
                } else {
                    current
                }

                val limit = if (normalized.isPremium) {
                    CHAT_PREMIUM_LIMIT
                } else {
                    CHAT_DAILY_LIMIT
                }

                val updated = if (normalized.dailyChatsUsed >= limit) {
                    normalized
                } else {
                    normalized.copy(
                        dailyChatsUsed = normalized.dailyChatsUsed + 1,
                        lastResetDate = normalized.lastResetDate ?: now,
                    )
                }

                if (!snapshot.exists || updated != current) {
                    set(
                        documentRef = ref,
                        strategy = AiQuotaDocument.serializer(),
                        data = updated,
                        merge = true,
                    )
                }

                updated.toState()
            }
        } catch (_: Exception) {
            null
        }
    }

    suspend fun consumePhoto(): AiQuotaState? {
        val uid = Firebase.auth.currentUser?.uid ?: return null
        val ref = firestore.document("$QUOTA_COLLECTION/$uid")

        return try {
            firestore.runTransaction {
                val snapshot = get(ref)
                val now = Timestamp.now()

                val current = if (snapshot.exists) {
                    snapshot.data(AiQuotaDocument.serializer())
                } else {
                    AiQuotaDocument(
                        lastResetDate = now,
                        createdAt = now,
                    )
                }

                val normalized = if (
                    current.lastResetDate == null ||
                    isNewLocalDay(current.lastResetDate)
                ) {
                    current.copy(
                        dailyChatsUsed = 0,
                        dailyPhotosUsed = 0,
                        lastResetDate = now,
                        createdAt = current.createdAt ?: now,
                    )
                } else {
                    current
                }

                val updated = if (normalized.dailyPhotosUsed >= PHOTO_DAILY_LIMIT) {
                    normalized
                } else {
                    normalized.copy(
                        dailyPhotosUsed = normalized.dailyPhotosUsed + 1,
                        lastResetDate = normalized.lastResetDate ?: now,
                    )
                }

                if (!snapshot.exists || updated != current) {
                    set(
                        documentRef = ref,
                        strategy = AiQuotaDocument.serializer(),
                        data = updated,
                        merge = true,
                    )
                }

                updated.toState()
            }
        } catch (_: Exception) {
            null
        }
    }

    private fun AiQuotaDocument.toState(): AiQuotaState {
        val limit = if (isPremium) CHAT_PREMIUM_LIMIT else CHAT_DAILY_LIMIT
        return AiQuotaState(
            chatsRemaining = (limit - dailyChatsUsed).coerceAtLeast(0),
            chatsTotal = limit,
            photosRemaining = (PHOTO_DAILY_LIMIT - dailyPhotosUsed).coerceAtLeast(0),
            photosTotal = PHOTO_DAILY_LIMIT,
            isPremium = isPremium,
        )
    }

    private fun isNewLocalDay(timestamp: Timestamp): Boolean {
        val zone = TimeZone.currentSystemDefault()
        val last = Instant.fromEpochSeconds(
            timestamp.seconds,
            timestamp.nanoseconds.toLong(),
        ).toLocalDateTime(zone).date
        val now = kotlin.time.Clock.System.now()
            .toLocalDateTime(zone)
            .date
        return last != now
    }
}
